import React, { useEffect, useMemo, useState } from 'react';
import PageHero from '../components/PageHero';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import { Turnstile } from '@marsidev/react-turnstile';
import { CONTACT_EMAILS, CONTACT_PHONES, OFFICE_LOCATIONS } from '../data/contact';
import { supabase } from '../utils/supabaseClient';

const INITIAL = {
  name: '',
  email: '',
  company: '',
  service: '',
  message: '',
  hpToken: '',
};

const INITIAL_TOUCHED = {
  name: false,
  email: false,
  company: false,
  service: false,
  message: false,
};

const DISPOSABLE_DOMAINS = [
  'mailinator.com',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'trashmail.com',
  'yopmail.com',
  'sharklasers.com',
  'temp-mail.org',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const LINK_REGEX = /(https?:\/\/|www\.)/gi;
const MAX_LINKS = 2;
const MAX_ATTEMPTS_BEFORE_COOLDOWN = 3;
const COOLDOWN_SECONDS = 30;
const MAX_TURNSTILE_AUTO_RETRIES = 2;

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [captchaToken, setCaptchaToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [cooldownTick, setCooldownTick] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
  const [turnstileAutoRetryCount, setTurnstileAutoRetryCount] = useState(0);
  const [turnstileLoadError, setTurnstileLoadError] = useState('');

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const supabaseEnabled = Boolean(supabase);
  const generalContactEmail = CONTACT_EMAILS.general;
  const usaOffice = OFFICE_LOCATIONS.find((loc) => loc.country === 'USA');
  const indiaOffice = OFFICE_LOCATIONS.find((loc) => loc.country === 'INDIA');
  const australiaOffice = OFFICE_LOCATIONS.find((loc) => loc.country === 'AUSTRALIA');

  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - cooldownTick) / 1000));
  const isCooldownActive = secondsLeft > 0;

  useEffect(() => {
    if (!isCooldownActive) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      setCooldownTick(Date.now());
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isCooldownActive]);

  const fieldErrors = useMemo(() => {
    const errors = {};
    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim().toLowerCase();
    const trimmedCompany = form.company.trim();
    const trimmedMessage = form.message.trim();
    const emailDomain = trimmedEmail.includes('@') ? trimmedEmail.split('@')[1] : '';
    const linkCount = (trimmedMessage.match(LINK_REGEX) || []).length;

    if (!trimmedName) {
      errors.name = 'Full name is required.';
    } else if (trimmedName.length < 2) {
      errors.name = 'Full name must be at least 2 characters.';
    } else if (trimmedName.length > 80) {
      errors.name = 'Full name must be under 80 characters.';
    }

    if (!trimmedEmail) {
      errors.email = 'Business email is required.';
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      errors.email = 'Enter a valid email address.';
    } else if (DISPOSABLE_DOMAINS.includes(emailDomain)) {
      errors.email = 'Please use a company or permanent email address.';
    }

    if (trimmedCompany.length > 120) {
      errors.company = 'Company must be under 120 characters.';
    }

    if (!form.service) {
      errors.service = 'Please select the service you are interested in.';
    }

    if (!trimmedMessage) {
      errors.message = 'Please describe your requirement.';
    } else if (trimmedMessage.length < 20) {
      errors.message = 'Message should be at least 20 characters.';
    } else if (trimmedMessage.length > 2000) {
      errors.message = 'Message should be under 2000 characters.';
    } else if (linkCount > MAX_LINKS) {
      errors.message = `Please include no more than ${MAX_LINKS} links.`;
    }

    return errors;
  }, [form]);

  const serviceUnavailableMessage = `Verification service is currently unavailable. Please try again later or email us directly at ${generalContactEmail}.`;

  const forceTurnstileRemount = () => {
    setCaptchaToken('');
    setTurnstileRenderKey((prev) => prev + 1);
  };

  const handleTurnstileRuntimeError = () => {
    setCaptchaToken('');

    if (turnstileAutoRetryCount < MAX_TURNSTILE_AUTO_RETRIES) {
      setTurnstileAutoRetryCount((prev) => prev + 1);
      setTurnstileRenderKey((prev) => prev + 1);
      return;
    }

    setTurnstileLoadError('CAPTCHA failed to load. Disable content blockers for this site and refresh CAPTCHA.');
  };

  const resetVerificationState = () => {
    setOtpCode('');
    setOtpSent(false);
    setStatusMessage('');
  };

  const toUserFriendlyError = (error) => {
    const rawMessage = (error?.message || '').toLowerCase();

    if (!rawMessage) {
      return serviceUnavailableMessage;
    }

    if (rawMessage.includes('timeout-or-duplicate') || (rawMessage.includes('timeout') && rawMessage.includes('duplicate'))) {
      return 'Challenge expired or already used. Please complete CAPTCHA again and retry.';
    }

    if (rawMessage.includes('captcha') || rawMessage.includes('turnstile')) {
      return 'CAPTCHA verification failed. Please refresh the challenge and try again.';
    }

    if (rawMessage.includes('otp') || rawMessage.includes('token')) {
      return 'The verification code is invalid or expired. Request a new OTP and try again.';
    }

    if (rawMessage.includes('network') || rawMessage.includes('fetch') || rawMessage.includes('failed to fetch')) {
      return serviceUnavailableMessage;
    }

    if (rawMessage.includes('database') || rawMessage.includes('relation') || rawMessage.includes('permission') || rawMessage.includes('row-level')) {
      return serviceUnavailableMessage;
    }

    return error.message || serviceUnavailableMessage;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'email' && prev.email.trim().toLowerCase() !== value.trim().toLowerCase()) {
        resetVerificationState();
      }

      return next;
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (name in INITIAL_TOUCHED) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const increaseAttempt = () => {
    const next = attemptCount + 1;
    setAttemptCount(next);
    if (next >= MAX_ATTEMPTS_BEFORE_COOLDOWN) {
      const now = Date.now();
      setCooldownTick(now);
      setCooldownUntil(now + COOLDOWN_SECONDS * 1000);
      setAttemptCount(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isCooldownActive) {
      setSubmitError(`Too many attempts. Please wait ${secondsLeft}s and try again.`);
      return;
    }

   if (form.hpToken.trim()) {
      setSubmitError('Submission blocked.');
      increaseAttempt();
      return;
   }

    setTouched({ ...INITIAL_TOUCHED, name: true, email: true, company: true, service: true, message: true });
    setSubmitError('');
   setStatusMessage('');

    if (Object.keys(fieldErrors).length > 0) {
      setSubmitError('Please fix the highlighted fields and try again.');
      increaseAttempt();
      return;
    }

    if (!supabaseEnabled || !turnstileSiteKey) {
      setSubmitError(serviceUnavailableMessage);
      increaseAttempt();
      return;
    }

    if (!captchaToken && !otpSent) {
      if (turnstileLoadError) {
        setSubmitError(turnstileLoadError);
      } else {
        setSubmitError('Please complete the CAPTCHA verification.');
      }
      increaseAttempt();
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedEmail = form.email.trim().toLowerCase();

      if (!otpSent) {
        const { error } = await supabase.auth.signInWithOtp({
          email: trimmedEmail,
          options: {
            shouldCreateUser: true,
            captchaToken,
          },
        });

        if (error) {
          throw error;
        }

        setOtpSent(true);
        setCaptchaToken('');
        setStatusMessage('Verification code sent. Check your email and enter the OTP to finish submitting your inquiry.');
        return;
      }

      if (!otpCode.trim()) {
        setSubmitError('Enter the OTP sent to your email.');
        increaseAttempt();
        return;
      }

      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: otpCode.trim(),
        type: 'email',
      });

      if (verifyError) {
        throw verifyError;
      }

      setStatusMessage('Email verified. Saving your inquiry...');

      const { error: insertError } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: form.name.trim(),
            email: trimmedEmail,
            company: form.company.trim(),
            service: form.service,
            message: form.message.trim(),
          },
        ]);

      if (insertError) {
        throw insertError;
      }

      const { error: notifyError } = await supabase.functions.invoke('contact-notify', {
        body: {
          name: form.name.trim(),
          email: trimmedEmail,
          company: form.company.trim(),
          service: form.service,
          message: form.message.trim(),
        },
      });

      if (notifyError) {
        console.error('Contact notify function error:', notifyError);
      }

      await supabase.auth.signOut();

      setSubmitted(true);
      setAttemptCount(0);
      setCooldownUntil(0);
    } catch (err) {
      setSubmitError(toUserFriendlyError(err));
      increaseAttempt();
      setCaptchaToken('');
      setTurnstileAutoRetryCount(0);
      setTurnstileLoadError('');
      setTurnstileRenderKey((prev) => prev + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const locationCards = [
    {
      country: 'USA',
      flag: usaOffice?.flag,
      address: usaOffice?.address,
      query: usaOffice?.query,
      phone: CONTACT_PHONES.usa,
      email: CONTACT_EMAILS.general,
    },
    {
      country: 'INDIA',
      flag: indiaOffice?.flag,
      address: indiaOffice?.address,
      query: indiaOffice?.query,
      phone: CONTACT_PHONES.india,
      email: 'info@tissatech.com',
    },
    {
      country: 'AUSTRALIA',
      flag: australiaOffice?.flag,
      address: australiaOffice?.address,
      query: australiaOffice?.query,
      phone: CONTACT_PHONES.australia,
      email: CONTACT_EMAILS.general,
    },
  ];

  return (
    <>
      <PageHero
        modifier="contact"
        kicker="Get in Touch"
        title="Contact Us"
        desc="Let's discuss your integration, ERP, analytics or cloud modernization goals."
      />
      <section className="section">
        <div className="container split">
          <div>
            <div className="kicker">Start a Conversation</div>
            <h2>Tell Us About Your Requirement</h2>
            <p>
              Our team can help identify a practical roadmap for SAP integration,
              Odoo ERP/CRM, analytics, and cloud engineering.
            </p>
            <ul className="check-list">
              <li>SAP integration assessment</li>
              <li>Odoo ERP/CRM implementation</li>
              <li>Data analytics modernization</li>
              <li>Cloud engineering advisory</li>
            </ul>
            <p style={{ marginTop: 24 }}>
              <b>Email:</b> <a href={`mailto:${CONTACT_EMAILS.general}`} style={{ color: 'var(--primary)' }}>{CONTACT_EMAILS.general}</a><br />
              <b>Website:</b> <a href="https://tissatech.com" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>tissatech.com</a>
            </p>
          </div>

          <div className="panel">
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <h3 style={{ color: 'var(--navy)' }}>Thank you!</h3>
                <p style={{ color: 'var(--muted)' }}>
                  We've received your inquiry and will be in touch shortly.
                </p>
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setForm(INITIAL);
                    setTouched(INITIAL_TOUCHED);
                    setCaptchaToken('');
                    resetVerificationState();
                    setSubmitError('');
                    setSubmitted(false);
                  }}
                  style={{ marginTop: 16 }}
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form className="form" onSubmit={handleSubmit} noValidate>
                <input
                  name="hpToken"
                  className="form-honeypot"
                  tabIndex="-1"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  value={form.hpToken}
                  onChange={handleChange}
                  aria-hidden="true"
                />
                <input
                  name="name" placeholder="Full Name" required
                  value={form.name} onChange={handleChange} onBlur={handleBlur}
                  aria-invalid={Boolean(touched.name && fieldErrors.name)}
                  aria-describedby={touched.name && fieldErrors.name ? 'contact-name-error' : undefined}
                />
                {touched.name && fieldErrors.name ? <p id="contact-name-error" className="form-error">{fieldErrors.name}</p> : null}
                <input
                  name="email" type="email" placeholder="Business Email" required
                  value={form.email} onChange={handleChange} onBlur={handleBlur}
                  aria-invalid={Boolean(touched.email && fieldErrors.email)}
                  aria-describedby={touched.email && fieldErrors.email ? 'contact-email-error' : undefined}
                />
                {touched.email && fieldErrors.email ? <p id="contact-email-error" className="form-error">{fieldErrors.email}</p> : null}
                <input
                  name="company" placeholder="Company"
                  value={form.company} onChange={handleChange} onBlur={handleBlur}
                  aria-invalid={Boolean(touched.company && fieldErrors.company)}
                  aria-describedby={touched.company && fieldErrors.company ? 'contact-company-error' : undefined}
                />
                {touched.company && fieldErrors.company ? <p id="contact-company-error" className="form-error">{fieldErrors.company}</p> : null}
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(touched.service && fieldErrors.service)}
                  aria-describedby={touched.service && fieldErrors.service ? 'contact-service-error' : undefined}
                >
                  <option value="" disabled hidden>Area of Interest</option>
                  <option>SAP Integration &amp; BTP</option>
                  <option>Integration Modernization (Boomi / iPaaS)</option>
                  <option>ERP &amp; CRM (Odoo / S/4 HANA)</option>
                  <option>Data &amp; Analytics</option>
                  <option>Cloud Engineering (AWS / GCP)</option>
                  <option>AI, ML &amp; Generative AI</option>
                  <option>Other / Not Sure Yet</option>
                </select>
                {touched.service && fieldErrors.service ? <p id="contact-service-error" className="form-error">{fieldErrors.service}</p> : null}
                <textarea
                  name="message"
                  placeholder="Tell us about your requirement"
                  required
                  value={form.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(touched.message && fieldErrors.message)}
                  aria-describedby={touched.message && fieldErrors.message ? 'contact-message-error' : undefined}
                />
                {touched.message && fieldErrors.message ? <p id="contact-message-error" className="form-error">{fieldErrors.message}</p> : null}

                <div className="form-captcha-wrap">
                  {turnstileSiteKey ? (
                    <Turnstile
                      key={turnstileRenderKey}
                      siteKey={turnstileSiteKey}
                      onSuccess={(token) => {
                        setCaptchaToken(token || '');
                        setTurnstileAutoRetryCount(0);
                        setTurnstileLoadError('');
                        if (submitError) {
                          setSubmitError('');
                        }
                      }}
                      onExpire={() => {
                        setTurnstileAutoRetryCount(0);
                        setTurnstileLoadError('');
                        forceTurnstileRemount();
                      }}
                      onError={() => {
                        handleTurnstileRuntimeError();
                      }}
                    />
                  ) : (
                    <p className="form-helper">
                      CAPTCHA is not configured. Set REACT_APP_TURNSTILE_SITE_KEY in your environment.
                    </p>
                  )}

                  {turnstileLoadError ? (
                    <div>
                      <p className="form-error" role="alert">{turnstileLoadError}</p>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => {
                          setTurnstileAutoRetryCount(0);
                          setTurnstileLoadError('');
                          forceTurnstileRemount();
                        }}
                      >
                        Retry CAPTCHA
                      </button>
                    </div>
                  ) : null}
                </div>

                {otpSent ? (
                  <>
                    <input
                      name="otp"
                      placeholder="Enter 6-digit OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      inputMode="numeric"
                      maxLength={6}
                      aria-label="Email OTP"
                    />
                  </>
                ) : null}

                {statusMessage ? (
                  <p className="form-helper" role="status">{statusMessage}</p>
                ) : null}

                {isCooldownActive ? (
                  <p className="form-helper" role="status">Too many attempts. Try again in {secondsLeft}s.</p>
                ) : null}

                {submitError ? (
                  <p className="form-error" role="alert">{submitError}</p>
                ) : null}

                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isSubmitting || isCooldownActive}
                >
                  {isSubmitting ? 'Working...' : otpSent ? 'Verify OTP & Submit' : 'Send OTP'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <section className="section section-soft">
        <div className="container">
          <div className="location-cards-heading-wrap" aria-hidden="true">
            <span className="location-cards-line" />
            <p className="location-cards-heading">Our Locations</p>
            <span className="location-cards-line" />
          </div>
        </div>
        <div className="container grid grid-3 animate-cards">
          {locationCards.map((loc) => {
            const officeMapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.query || '')}`;
            return (
              <article className="card card--location-contact" key={loc.country}>
                <div className="location-flag-wrap">
                  <img className="location-flag-img" src={loc.flag} alt={`${loc.country} flag`} loading="lazy" />
                </div>
                <h4>{loc.country}</h4>
                <p className="location-card-address">
                  <a className="location-card-link" href={officeMapUrl} target="_blank" rel="noreferrer">
                    {loc.address}
                  </a>
                </p>
                <p className="location-card-detail">
                  <a className="location-card-link" href={`tel:${loc.phone.tel}`}>{loc.phone.display}</a>
                </p>
                <p className="location-card-detail">
                  <a className="location-card-link" href={`mailto:${loc.email}`}>{loc.email}</a>
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section location-maps">
        <div className="container">
          <div className="kicker">Find Us</div>
          <h2>Our Locations on Map</h2>
          {(() => {
            return (
              <article className="location-map-card location-map-card--single">
                <div className="location-map-head">
                  <h3>USA, INDIA &amp; AUSTRALIA</h3>
                </div>

                <div className="location-map-links">
                  {OFFICE_LOCATIONS.map((loc) => {
                    const openUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.query)}`;
                    return (
                      <a key={loc.country} href={openUrl} target="_blank" rel="noreferrer">
                        {loc.country} - Open in Maps
                      </a>
                    );
                  })}
                </div>

                <div className="location-map-frame">
                  <MapContainer
                    center={[20, 40]}
                    zoom={2}
                    minZoom={2}
                    scrollWheelZoom={false}
                    className="location-map-leaflet"
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {OFFICE_LOCATIONS.map((loc) => (
                      <CircleMarker
                        key={loc.country}
                        center={loc.position}
                        radius={8}
                        pathOptions={{ color: '#0f4ec9', fillColor: '#2563eb', fillOpacity: 0.9, weight: 2 }}
                      >
                        <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                          <strong>{loc.country}</strong><br />
                          {loc.address}
                        </Tooltip>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                </div>
              </article>
            );
          })()}
        </div>
      </section>
    </>
  );
}
